import { collection, addDoc, getDocs, getDoc, doc, updateDoc, deleteDoc, query, where, limit, serverTimestamp } from 'firebase/firestore'
import { db } from '../config/firebase'
import type { SavedConfiguration, ConfigurationState, PricingData } from '../types'

class SavedConfigurationsService {
  private collectionName = 'savedConfigurations'

  async saveConfiguration(
    userId: string,
    name: string,
    modelName: string,
    modelKey: string,
    modelGroup: string,
    productCode: string,
    configuration: ConfigurationState,
    pricing: PricingData,
    clientNotes?: string
  ): Promise<SavedConfiguration> {
    const payload = {
      name: name.trim(),
      userId,
      modelName,
      modelKey,
      modelGroup,
      productCode,
      configuration,
      pricing,
      clientNotes: clientNotes?.trim() || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    const docRef = await addDoc(collection(db, this.collectionName), payload)
    return { id: docRef.id, ...payload } as SavedConfiguration
  }

  async getUserConfigurations(userId: string): Promise<SavedConfiguration[]> {
    const q = query(
      collection(db, this.collectionName),
      where('userId', '==', userId)
      // Temporarily removed orderBy to avoid index requirement
      // orderBy('updatedAt', 'desc')
    )
    
    const snap = await getDocs(q)
    const configs = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as SavedConfiguration[]
    
    // Sort in JavaScript instead
    return configs.sort((a, b) => {
      const aDate = a.updatedAt ? new Date(a.updatedAt as any).getTime() : 0
      const bDate = b.updatedAt ? new Date(b.updatedAt as any).getTime() : 0
      return bDate - aDate // Descending order (newest first)
    })
  }

  async updateConfigurationName(configId: string, newName: string): Promise<void> {
    const configRef = doc(db, this.collectionName, configId)
    await updateDoc(configRef, {
      name: newName.trim(),
      updatedAt: serverTimestamp(),
    })
  }

  async deleteConfiguration(configId: string): Promise<void> {
    await deleteDoc(doc(db, this.collectionName, configId))
  }

  async duplicateConfiguration(configId: string, newName: string): Promise<SavedConfiguration> {
    // Pobierz oryginalną konfigurację - używamy getDoc zamiast query dla wydajności
    const configDocRef = doc(db, this.collectionName, configId)
    const docSnap = await getDoc(configDocRef)
    
    if (!docSnap.exists()) {
      throw new Error('Konfiguracja nie została znaleziona')
    }
    
    const originalData = docSnap.data() as SavedConfiguration
    
    // Utwórz kopię z nową nazwą
    const payload = {
      ...originalData,
      name: newName.trim(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    
    delete (payload as any).id // Usuń ID żeby utworzyć nowy dokument
    
    const docRef = await addDoc(collection(db, this.collectionName), payload)
    return { id: docRef.id, ...payload } as SavedConfiguration
  }

  async searchUserConfigurations(userId: string, searchTerm: string, limitCount: number = 100): Promise<SavedConfiguration[]> {
    const term = searchTerm.toLowerCase().trim()
    if (!term) return []

    try {
      // Pobierz wszystkie konfiguracje użytkownika (będziemy filtrować po stronie klienta)
      // Firestore nie obsługuje pełnotekstowego wyszukiwania, więc musimy pobrać wszystkie i przefiltrować
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        limit(limitCount * 2) // Pobierz więcej żeby mieć zapas po filtrowaniu
      )
      
      const snap = await getDocs(q)
      const allConfigs = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as SavedConfiguration[]
      
      // Filtruj po nazwie, modelu i notatkach
      const matchingConfigs = allConfigs.filter(config => {
        const searchFields = [
          config.name || '',
          config.modelName || '',
          config.clientNotes || '',
          config.productCode || ''
        ].map(field => field.toLowerCase())
        
        return searchFields.some(field => field.includes(term))
      })
      
      // Sortuj jak w getUserConfigurations
      return matchingConfigs
        .sort((a, b) => {
          // Exact match na początku
          const aNameExact = (a.name || '').toLowerCase() === term
          const bNameExact = (b.name || '').toLowerCase() === term
          
          if (aNameExact && !bNameExact) return -1
          if (!aNameExact && bNameExact) return 1
          
          // Potem po dacie
          const aDate = a.updatedAt ? new Date(a.updatedAt as any).getTime() : 0
          const bDate = b.updatedAt ? new Date(b.updatedAt as any).getTime() : 0
          return bDate - aDate
        })
        .slice(0, limitCount) // Obetnij do żądanej liczby
        
    } catch (error) {
      console.error('Błąd wyszukiwania konfiguracji:', error)
      return []
    }
  }
}

export default new SavedConfigurationsService()